import React from "react"
import { Link } from "gatsby"
import { Helmet } from "react-helmet"

import Layout from "../components/Layout"
import CtaBanner from "../components/WEB_CtaBanner"
import { offers, isGated } from "../data/offers"

import "../styles/services.scss" // .web-cta-banner lives here
import "../styles/solutions.scss"

const SITE = "https://www.visiomate.com"

/**
 * /offers/ — the whole catalogue.
 *
 * Was /solutions/, and was listing 5 of 19. Two changes:
 *
 *   The path. These are offers — a fixed piece of work with a scope and a
 *   date — and the URL now says so. /solutions/ was never submitted to Google
 *   (CONTENT-ARCHITECTURE §0 lists it as still in development), so nothing
 *   indexed moved and no redirect is needed.
 *
 *   The contents. It read from solutions.js, which is offers.filter(o => o.live),
 *   so the thirteen offers without a page were invisible on the one page whose
 *   job is to show what Visiomate sells. It now reads the catalogue directly.
 *
 * Rows link to the offer page where one exists. Where one does not, the row
 * opens the pricing gate — which is the same destination /pricing/ uses, and a
 * better one than /contact because it is what the visitor actually wants.
 */

/**
 * Groups, keyed on `displayGroup`, in the order the catalogue is organised.
 *
 * NOT `group` — that field is the catalogue letter (A–F). Filtering on it is
 * what made this page render empty once already: every key matched nothing, and
 * it shipped because the check confirmed the route returned 200 rather than
 * that it still had rows. The assertion below is the guard against a repeat.
 *
 * `capacity` and `product` are here because the catalogue has them. Without
 * them the four ongoing and product offers would silently vanish.
 */
const GROUPS = [
  { key: "entry", eyebrow: "Diagnostic", label: "Start here" },
  { key: "ai", eyebrow: "Narrow AI", label: "AI systems" },
  { key: "odoo", eyebrow: "ERP", label: "Odoo implementations" },
  { key: "flagship", eyebrow: "End to end", label: "Full platforms" },
  { key: "product", eyebrow: "Subscription", label: "Products" },
  { key: "capacity", eyebrow: "After go-live", label: "Ongoing capacity" },
]

// Never on this site (CONTENT-ARCHITECTURE §0). Named so the check below can
// tell "excluded on purpose" from "forgotten".
const EXCLUDED = ["civic-campaign-platform"]

const LISTED = offers.filter(o => !EXCLUDED.includes(o.slug))

// Build-time guard. A missing group is invisible in a route check — the page
// still returns 200 — so the failure has to be asserted rather than eyeballed.
const grouped = GROUPS.reduce(
  (n, g) => n + LISTED.filter(o => o.displayGroup === g.key).length,
  0
)
if (grouped !== LISTED.length) {
  // eslint-disable-next-line no-console
  console.warn(
    `[offers] ${LISTED.length - grouped} offer(s) match no group and will not render. ` +
      `GROUPS keys must match the displayGroup values in src/data/offers.js.`
  )
}

const OffersHub = () => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE },
      { "@type": "ListItem", position: 2, name: "Offers", item: `${SITE}/offers` },
    ],
  }

  return (
    <Layout
      pageTitle="Fixed-Scope Offers, Live in Weeks | Visiomate"
      // Was "Five fixed-scope solutions... Each has a published price". Both
      // halves are now wrong: there are eighteen, and only the three diagnostic
      // offers publish a figure.
      descrip="Eighteen fixed-scope offers across Odoo, field service, healthcare, AI and ongoing capacity. Every one has a fixed timeline and a written scope."
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="solution-page">
        {/* ── Hero ── */}
        <section className="sol-hero">
          <div className="sol-hero__blob sol-hero__blob--1" />
          <div className="sol-hero__blob sol-hero__blob--2" />
          <div className="sol-hero__blob sol-hero__blob--3" />
          <div className="sol-hero__grain" />
          <div className="sol-hero__overlay" />
          <div className="sol-hero__content">
            <nav className="sol-hero__crumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>Offers</span>
            </nav>
            <h1 className="sol-hero__title">Fixed-scope offers, live in weeks</h1>
            {/* Rewritten again. It ended "the scope and the price arrive
                together in one document" — there is no document; the price is
                revealed on the offer page itself. */}
            <p className="sol-hero__lead">
              Every offer below has a fixed timeline and a written scope agreed
              at kickoff that does not move afterwards. The three diagnostic
              offers cost exactly what they say; open any of the others to see
              its complete scope and pricing.
            </p>
            <Link to="/contact" className="sol-btn sol-btn--primary">
              Book a fit call
            </Link>
          </div>
        </section>

        {/* ── The catalogue — one compact list, scannable in a screen (§7.1) ── */}
        <section className="sol-section">
          <div className="sol-wrap">
            {GROUPS.map(group => {
              const items = LISTED.filter(o => o.displayGroup === group.key)
              if (!items.length) return null

              return (
                <div key={group.key} className="sol-group">
                  <span className="sol-group__eyebrow">{group.eyebrow}</span>
                  <h2 className="sol-group__heading">{group.label}</h2>

                  <ul className="sol-rows">
                    {items.map(o => {
                      const gated = isGated(o)

                      // Every offer has a page now, so every row is a link.
                      // The gate that used to sit under gated rows is gone: the
                      // row already goes somewhere, and a button beside a link
                      // that leads to the same place is two controls for one
                      // intent. What replaces it is a HINT — plain text inside
                      // the anchor, not a control of its own — saying what a
                      // click will show.
                      const RowInner = (
                        <>
                          <span className="sol-row__main">
                            <span className="sol-row__name">{o.name}</span>
                            <span className="sol-row__problem">
                              {o.symptoms[0]}
                            </span>
                            {gated && (
                              <span className="sol-row__hint">
                                See the complete scope and pricing
                              </span>
                            )}
                          </span>
                          <span className="sol-row__meta">
                            <span className="sol-row__timeline">
                              {o.timelineShort}
                            </span>
                            {/* The three diagnostic offers publish a figure and
                                always have. NO GATED PRICE APPEARS HERE — not
                                even once the visitor has unlocked. The reveal
                                belongs on the offer page, where the figure sits
                                next to the scope that gives it meaning; a price
                                in a catalogue row is a number with nothing
                                attached. This is why the row carries a hint
                                rather than a figure. */}
                            {!gated && (
                              <span className="sol-row__price">
                                {o.priceShort}
                              </span>
                            )}
                          </span>
                        </>
                      )

                      return (
                        <li key={o.slug} className="sol-rows__item">
                          <Link to={`/offers/${o.slug}`} className="sol-row">
                            {RowInner}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })}

            {/* The "every price in one table" link pointed at /pricing/, which
                is gone — this page is the table now. A link from the catalogue
                to itself is not worth keeping, so the line goes with it. */}
          </div>
        </section>

        <CtaBanner />
      </div>
    </Layout>
  )
}

export default OffersHub
